import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, lowerCase, sumBy, times } from 'lodash-es';
import T from 'prop-types';
import cx from 'classnames';

import Skeleton from '../../../../components/Skeleton';
import { seriesColor } from '../../review-analysis/shared/config';
import { numberToHumanChart } from '../../../../utils/formatter';

const skeletons = {
  reviewsChart: (
    <div className="reviews-block">
      <div className="reviews-body">
        {times(3).map((n) => (
          <Skeleton key={n} height={n === 0 || n === 2 ? 32 : 20} width={n === 0 ? 150 : '100%'} />
        ))}
      </div>
      <div className="intents-body">
        {times(3).map((n) => (
          <Skeleton key={n} height={n === 0 || n === 2 ? 32 : 20} width={n === 0 ? 150 : '100%'} />
        ))}
      </div>
    </div>
  ),
};

const trendsColors = {
  'performance-and-bugs': '#3E8EDE',
  design: '#A1CDF7',
  service: '#45B068',
  login: '#AFE09D',
  'feature-request': '#E56389',
  support: '#F7ABB7',
  payment: '#A26CBD',
  notifications: '#CCB0EB',
};

const forEmptyArray = [54, 43, 21, 12];
const emptyText = 'No reviews for current filter selection';

const Reviews = ({ sentiments, intents, reviewsList, isLoaded, isPending }) => {
  const legendRef = useRef();
  const [isLegendFit, setLegendFit] = useState(true);
  const emptySentiments =
    isEmpty(sentiments) || sentiments.every(({ percentage }) => percentage === 0);
  const emptyIntents = isEmpty(intents) || intents.every(({ percentage }) => percentage === 0);

  useEffect(() => {
    if (isEmpty(legendRef.current)) return () => true;
    const totalChildrenWidth = sumBy([...legendRef.current.children], 'clientWidth');
    setLegendFit(legendRef.current.clientWidth > totalChildrenWidth + 16 * 4); // + gaps
  }, [intents, sentiments, isLoaded]);

  const chartByType = {
    sentiment: (
      <div className="intents-body__chart">
        {!emptySentiments
          ? sentiments.map(
              ({ name, percentage }) =>
                !!percentage && (
                  <div
                    key={name}
                    className="reviews-body__chart-item"
                    style={{
                      width: `${percentage}%`,
                      background: name && seriesColor[lowerCase(name)],
                    }}
                  />
                )
            )
          : forEmptyArray.map((percentage) => (
              <div
                key={percentage}
                className="reviews-body__chart-item"
                style={{ width: `${percentage}%`, background: '#E1E5EB' }}
              />
            ))}
      </div>
    ),
    intent: (
      <div className="intents-body__chart">
        {!emptyIntents
          ? intents.map(({ name, percentage, metric }) => (
              <div
                key={name}
                className="intents-body__chart-item"
                style={{ width: `${percentage}%`, background: percentage && trendsColors[metric] }}
              />
            ))
          : forEmptyArray.map((percentage) => (
              <div
                key={percentage}
                className="reviews-body__chart-item"
                style={{ width: `${percentage}%`, background: '#E1E5EB' }}
              />
            ))}
      </div>
    ),
  };

  const chartLegendByType = {
    sentiment: (
      <div className="reviews-body__chart-legend">
        {!emptySentiments ? (
          sentiments.map(
            ({ name, percentage, value }) =>
              !!percentage && (
                <div key={name} className="reviews-body__chart-legend-item">
                  <div
                    className="reviews-body__chart-legend-item-bullet"
                    style={{ background: name && seriesColor[lowerCase(name)] }}
                  />
                  {name}
                  <span className="reviews-body__chart-legend-item-number">
                    ({numberToHumanChart(value, { precision: 2 })})
                  </span>
                </div>
              )
          )
        ) : (
          <p className="paragraph-sm">{emptyText}</p>
        )}
      </div>
    ),

    intent: (
      <div className="intents-body__chart-legend" ref={legendRef}>
        {!emptyIntents ? (
          intents.map(({ name, value, metric }, i) => (
            <div
              key={name}
              className={cx('intents-body__chart-legend-item', { hidden: !isLegendFit && i === 3 })}
            >
              <div
                className="intents-body__chart-legend-item-bullet"
                style={{ background: name && trendsColors[metric] }}
              />
              {name}
              <span className="intents-body__chart-legend-item-number">
                ({numberToHumanChart(value, { precision: 2 })})
              </span>
            </div>
          ))
        ) : (
          <p className="paragraph-sm">{emptyText}</p>
        )}
        {!emptyIntents && (
          <div className="intents-body__chart-legend-item">
            <span className="intents-body__chart-legend-item-number">
              +{isLegendFit ? 4 : 5} More
            </span>
          </div>
        )}
      </div>
    ),
  };

  const body = useMemo(() => {
    if (isPending) return skeletons.reviewsChart;
    return (
      <div className="reviews-block">
        {!isEmpty(reviewsList) &&
          reviewsList.map(({ type, description, title }) => (
            <div key={type} className="intents-body">
              <div className="intents-body__text">
                <div className="intents-body__text-heading h5 text-black">{title}</div>
                <div className="intents-body__text-description paragraph-md">{description}</div>
              </div>
              {chartByType[type]}
              {chartLegendByType[type]}
            </div>
          ))}
      </div>
    );
  }, [intents, sentiments, isLegendFit]);

  return body;
};

Reviews.propTypes = {
  sentiments: T.array.isRequired,
  intents: T.array.isRequired,
  reviewsList: T.array.isRequired,
  isLoaded: T.bool,
  isPending: T.bool,
};

export default Reviews;
